/**
 * Renderers of the interactive-input palette (KAN-1703). A visualization block
 * asks for the controls declared on it and gets them here; which port a control
 * drives is the declaration's business, never this file's.
 */
import { Button, IngotIcon } from "@/ingot";
import { useTranslation } from "react-i18next";

import { resolveI18nText } from "@/components/admin/I18nField";

import {
  inputsForView,
  QUARTER_TURNS_3D,
  quarterTurnsOf,
  type InteractiveBinding,
  type InteractiveInputState,
} from "./interactiveInputs";

interface ControlProps {
  input: InteractiveInputState;
  onChange: (value: unknown) => void;
}

const AXES = ["x", "y", "z"] as const;

/** ``quarter_turns_3d``: one +90° button per axis and "back to recommended". */
function QuarterTurns3dControl({ input, onChange }: ControlProps): JSX.Element {
  const { t, i18n } = useTranslation("quote");
  const current = quarterTurnsOf(input.value) ??
    quarterTurnsOf(input.default) ?? { x: 0, y: 0, z: 0 };
  const recommended = quarterTurnsOf(input.default);
  const isRecommended =
    recommended !== null &&
    AXES.every((axis) => current[axis] === recommended[axis]);
  // Port label from the module's manifest, else the control's own caption.
  const label =
    resolveI18nText({ cs: input.label_cs ?? "", en: input.label_en ?? "" }, i18n.language) ||
    t("interactiveControls.quarter_turns_3d.label");

  const turn = (axis: (typeof AXES)[number]) => {
    const next = { ...current, [axis]: (current[axis] + 1) % 4 };
    onChange([next.x, next.y, next.z]);
  };

  return (
    <div
      role="group"
      aria-label={label}
      className="flex flex-wrap items-center gap-2"
      data-testid={`interactive-control-${input.key}`}
    >
      <span className="text-sm text-ink-2">{label}</span>
      {AXES.map((axis) => (
        <Button
          key={axis}
          size="sm"
          variant="secondary"
          onClick={() => turn(axis)}
          data-testid={`interactive-control-${input.key}-${axis}`}
        >
          {t(`interactiveControls.quarter_turns_3d.rotate_${axis}`)}
        </Button>
      ))}
      {recommended !== null && (
        <Button
          size="sm"
          variant="ghost"
          disabled={isRecommended}
          onClick={() => onChange(null)}
          data-testid={`interactive-control-${input.key}-recommended`}
        >
          <IngotIcon name="reset" />
          {t("interactiveControls.quarter_turns_3d.recommended")}
        </Button>
      )}
    </div>
  );
}

const CONTROL_RENDERERS: Record<string, (props: ControlProps) => JSX.Element> = {
  [QUARTER_TURNS_3D]: (props) => <QuarterTurns3dControl {...props} />,
};

/** Controls declared on ``viewKind``; nothing when none are (the block stays a
 *  plain view — a module without ``interactive_inputs`` sees no change). */
export function InteractiveControls({
  binding,
  viewKind,
}: {
  binding?: InteractiveBinding;
  viewKind: string;
}): JSX.Element | null {
  const { t } = useTranslation("quote");
  const inputs = inputsForView(binding?.inputs, viewKind);
  if (!binding || inputs.length === 0) return null;
  return (
    <div className="space-y-2 border-t border-border p-3" data-testid="interactive-controls">
      {inputs.map((input) => {
        const render = CONTROL_RENDERERS[input.control];
        return render ? (
          <div key={input.key}>
            {render({
              input,
              onChange: (value) => binding.onChange(input.key, value),
            })}
          </div>
        ) : null;
      })}
      {binding.failed && (
        <p className="text-xs text-danger" role="alert" data-testid="interactive-controls-failed">
          {t("interactiveControls.failed")}
        </p>
      )}
    </div>
  );
}
